import { useRef, useState } from "react";
import { toast } from "sonner";
import { Activity, FileUp, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";

export const TelemetryUpload = ({ sessionId, stint, current, onChange }) => {
  const { t } = useT();
  const ref = useRef(null);
  const [progress, setProgress] = useState(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setProgress(0);
    try {
      const { data } = await api.post(`/sessions/${sessionId}/telemetry`, fd, {
        params: { stint },
        onUploadProgress: (ev) => setProgress(ev.total ? Math.round((ev.loaded / ev.total) * 100) : null),
      });
      const upd = data.session_update || {};
      toast.success(upd.lap_data || upd.lap_data2 ? t("telemetry_imported_laps") : t("telemetry_imported"));
      onChange(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setProgress(null);
    }
  };

  const remove = async () => {
    await api.delete(`/telemetry/${current.id}`);
    toast.success(t("deleted"));
    onChange(null);
  };

  return (
    <div data-testid={`telemetry-upload-${stint}`} className="flex flex-wrap items-center gap-3 bg-[#090A0F] border border-dashed border-[#282D3D] rounded-sm p-4">
      <input ref={ref} type="file" accept=".csv,.CSV,text/csv,text/plain" className="hidden" data-testid={`telemetry-file-input-${stint}`} onChange={onFile} />
      {current ? (
        <div className="flex items-center gap-2 font-mono text-sm min-w-0">
          <Activity className="w-4 h-4 text-[#10B981] shrink-0" />
          <span data-testid="telemetry-file-name" className="truncate">{current.filename}</span>
          <span className="text-slate-500 text-xs">· {current.laps.filter((l) => l.full).length} {t("laps_short")} · {current.channels.join(", ")}</span>
        </div>
      ) : (
        <p className="text-sm text-slate-400">{t("telemetry_hint")}</p>
      )}
      <div className="ml-auto flex items-center gap-2">
        {progress != null && <span data-testid="telemetry-progress" className="font-mono text-xs text-[#FFB800]">{progress < 100 ? `${progress}%` : t("processing")}</span>}
        <Button type="button" disabled={progress != null} data-testid={`telemetry-import-btn-${stint}`} onClick={() => ref.current?.click()}
          className="rounded-sm bg-[#FF3B30] hover:bg-[#e0332a] font-heading uppercase tracking-wide text-base h-10">
          <FileUp className="w-4 h-4 mr-1.5" /> {current ? t("replace_csv") : t("import_telemetry")}
        </Button>
        {current && (
          <Button type="button" variant="outline" data-testid={`telemetry-remove-btn-${stint}`} onClick={remove}
            className="rounded-sm bg-transparent border-[#282D3D] text-slate-400 hover:text-[#FF3B30] hover:border-[#FF3B30] hover:bg-transparent h-10">
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};
